import styles from "./LocationGyms.module.scss";
import "../../components/ui/FormItemSelect/FormItemSelect.scss";
import { useState } from "react";
import { Helmet } from "react-helmet";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { InfoWindow, Marker, MarkerClusterer } from "@react-google-maps/api";
import useAppSelector from "../../hooks/useAppSelector";
import Map from "../../components/Map/Map";
import Loader from "../../components/shared/Loader/Loader";
import LocationGymsFilter from "./LocationGymsFilter/LocationGymsFilter";
import markIcon from "../../assets/images/icons/main-marker.svg";
import { defaultCenterForMap } from "../../components/Map/themeMap";
import { textCropping } from "../../helper/language";
import { useGetAllApprovedGymsQuery } from "../../api/queries";

import { IGymListing } from "../../models/GymListing";
import { useGetAllClassCategoriesQuery } from "../../api/queries/classEvent/useGetAllClassCategoriesQuery";

const LocationGyms = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { location } = useAppSelector((state) => state.userLocationSlice);

  const [selectedFacilities, setSelectedFacilities] = useState<string[]>([]);
  const [selectedDistance, setSelectedDistance] = useState<[number, number]>([0, 20]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [approvedParams, setApprovedParams] = useState<any>(null);

  const [mapCenter, setMapCenter] = useState({
    lat: location?.lat ?? defaultCenterForMap.lat,
    lng: location?.lng ?? defaultCenterForMap.lng,
  });
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);

  const { data: gyms = [], isLoading } = useGetAllApprovedGymsQuery(approvedParams, null);
  const { data: classCategories = [] } = useGetAllClassCategoriesQuery();

  const onSearch = () => {
    setApprovedParams({
      userLocationLat: mapCenter.lat,
      userLocationLng: mapCenter.lng,
      facilities: selectedFacilities,
      categoryIds: selectedCategories,
      distanceMin: selectedDistance[0],
      distanceMax: selectedDistance[1],
    });
  };

  const onResetAll = () => {
    setSelectedFacilities([]);
    setSelectedCategories([]);
    setSelectedDistance([0, 20]);
    setApprovedParams({});
  };

  const onClickInGym = (gym: IGymListing) => {
    navigate(`/gyms-location/${gym.id}/classes`, {
      state: { from: "location-gyms", gym },
    });
  };
  const onClickInGymFromMap = (id: string) => navigate(`/gyms-location/${id}`);

  const renderGymItem = (gym: IGymListing) => (
    <div key={gym.id} className={styles.gymItem} onClick={() => onClickInGym(gym)}>
      <div className={styles.left}>
        <div
          className={styles.img}
          style={{ backgroundImage: `url("${gym.imagePaths[0]}")` }}
        ></div>

        <div className={styles.info}>
          <p className={styles.services}>
            {JSON.parse(gym.facilityOffers)
              .map((el: string) => t(el))
              .slice(0, 2)
              .join(", ")}
          </p>
          <p className={styles.name}>{gym.businessName}</p>
          <p className={styles.location}>
            {gym.country}, {gym.city}, {gym.street}, {gym.streetNumber}
          </p>
        </div>
      </div>

      <div className={styles.right}>
        <p className={styles.description}>{textCropping(gym.description, 170)}</p>
      </div>
    </div>
  );

  const renderGymList = () => (isLoading ? <Loader /> : gyms.map(renderGymItem));

  return (
    <>
      <Helmet>
        <title>{t("LocationsAccessTopFitnessCentersNearYouWithClubPass")}</title>
        <meta name="description" content={t("ExploreTopFitnessCentersNearYouWithClubPass")} />
      </Helmet>

      <div className={styles.container}>
        <div className={styles.top}>
          <div className="container">
            <LocationGymsFilter
              allCategories={classCategories}
              categories={selectedCategories}
              amenities={selectedFacilities}
              distance={selectedDistance}
              onChangeAmenities={setSelectedFacilities}
              onSearch={onSearch}
              onChangeDistance={setSelectedDistance}
              onChangeCategories={setSelectedCategories}
              onResetAll={onResetAll}
            />
          </div>
        </div>

        <div className={styles.wrapper}>
          <div className={styles.gymInfo}>{renderGymList()}</div>

          <div className={styles.map}>
            <Map center={mapCenter}>
              <MarkerClusterer
                options={{
                  styles: [
                    {
                      url: "/images/common/orange-circle.png",
                      height: 40,
                      width: 40,
                      textColor: "white",
                      textSize: 14,
                    },
                  ],
                }}
              >
                {(clusterer) => (
                  <>
                    {gyms.map((el) => (
                      <Marker
                        key={el.id}
                        position={{ lat: el.lat, lng: el.long }}
                        clusterer={clusterer}
                        icon={markIcon}
                        onClick={() => onClickInGymFromMap(el.id)}
                        onMouseOver={() => setHoveredMarkerId(el.id)}
                        onMouseOut={() => setHoveredMarkerId(null)}
                      />
                    ))}

                    {gyms
                      .filter((el) => hoveredMarkerId === el.id)
                      .map((el) => (
                        <InfoWindow
                          key={`info-${el.id}`}
                          position={{ lat: el.lat, lng: el.long }}
                          options={{
                            disableAutoPan: false,
                            pixelOffset: new window.google.maps.Size(0, -30),
                            headerDisabled: true,
                          }}
                        >
                          <div className={styles.hoverBox}>
                            <div
                              className={styles.image}
                              style={{ backgroundImage: `url("${el.imagePaths[0]}")` }}
                            />
                            <div className={styles.services}>
                              {JSON.parse(el.facilityOffers)
                                .map((x: string) => t(x))
                                .slice(0, 2)
                                .join(", ")}
                            </div>
                            <h3 className={styles.title}>{el.businessName}</h3>
                            <p className={styles.address}>
                              {el.street} {el.streetNumber}, {el.city}
                            </p>
                          </div>
                        </InfoWindow>
                      ))}
                  </>
                )}
              </MarkerClusterer>
            </Map>
          </div>
        </div>
      </div>
    </>
  );
};

export default LocationGyms;
